import { INVITE_PATH_PREFIX } from "@pax/api-contract/waitlist/v1alpha1";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { OfflineStatus } from "@/components/OfflineStatus";
import { NotFound, RouteError } from "@/components/RouteError";
import {
  type DevelopmentTool,
  readDevelopmentToolsVisibility,
  writeDevelopmentToolVisibility,
} from "@/development/preferences";
import {
  ACCOUNT_DELETED_PATH,
  AccountDeletionProvider,
  isDeletionPending,
} from "@/features/auth/account-deletion";
import { AccountDialogProvider } from "@/features/auth/account-dialog";
import { AccountDeletionLock } from "@/features/auth/components/AccountDeletionPage";
import { EntryScreen } from "@/features/auth/components/EntryScreen";
import { clientConfigQuery, isWaitlisted, sessionQuery, useSession } from "@/features/auth/queries";
import { TopUpProvider } from "@/features/credits/top-up";
import { isCheckPending } from "@/features/risk/CheckingSession";
import { RiskDialog } from "@/features/risk/RiskDialog";
import { WaitlistScreen } from "@/features/waitlist/WaitlistScreen";
import { useConnectionStatus } from "@/lib/network";
import { AppShell } from "@/shell/AppShell";

/**
 * Development tooling only ever ships in development; a production build
 * drops these branches and never requests the modules.
 */
const Devtools = import.meta.env.DEV
  ? lazy(() => import("@/development/Devtools").then(m => ({ default: m.Devtools })))
  : null;

const BotChecks = import.meta.env.DEV
  ? lazy(() => import("@/development/BotChecks").then(m => ({ default: m.BotChecks })))
  : null;

function setReactScanVisible(visible: boolean) {
  if (import.meta.env.DEV) {
    void import("@/development/react-scan").then(m => m.setReactScanVisible(visible));
  }
}

function setReactGrabVisible(visible: boolean) {
  if (import.meta.env.DEV) {
    void import("@/development/react-grab").then(m => m.setReactGrabVisible(visible));
  }
}

function setViteDevtoolsVisible(visible: boolean) {
  if (import.meta.env.DEV) {
    void import("@/development/vite-devtools").then(m => m.setViteDevtoolsVisible(visible));
  }
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({ meta: [{ title: "Pax.co" }] }),
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    guestUpgrade?: "success" | "failed";
    error?: string;
  } => ({
    guestUpgrade:
      search.guestUpgrade === "success" || search.guestUpgrade === "failed"
        ? search.guestUpgrade
        : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  // Nothing draws until the session and client config are known, because which screen comes first
  // depends on them: sign-in, the check, or Pax. A read that fails leaves the shell to explain.
  beforeLoad: async ({ context: { queryClient } }) => {
    await Promise.allSettled([
      queryClient.ensureQueryData(sessionQuery),
      queryClient.ensureQueryData(clientConfigQuery),
    ]);
  },
  component: RootLayout,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

/**
 * Everything that is not a screen: where you can go, what is yours, and the
 * frame both of those are drawn on. `AppShell` owns all of it.
 *
 * The window itself never scrolls. The screen is the app's single scroll
 * container, and a route that wants to fill it uses `h-full`.
 */
function RootLayout() {
  const navigate = Route.useNavigate();
  const href = useLocation({ select: location => location.href });
  const pathname = useLocation({ select: location => location.pathname });
  const { guestUpgrade, error } = Route.useSearch();
  const connectionStatus = useConnectionStatus();
  const { data: session } = useSession();
  const { data: clientConfig } = useQuery(clientConfigQuery);
  const checkPending = isCheckPending(session, Boolean(clientConfig?.castlePublishableKey));
  const [developmentTools, setDevelopmentTools] = useState(readDevelopmentToolsVisibility);

  function dismissGuestUpgradeResult() {
    const target = new URL(href, window.location.origin);
    target.searchParams.delete("guestUpgrade");
    target.searchParams.delete("error");
    void navigate({
      href: `${target.pathname}${target.search}${target.hash}`,
      replace: true,
    });
  }

  function handleDevelopmentToolVisibilityChange(tool: DevelopmentTool, visible: boolean) {
    setDevelopmentTools(current => ({ ...current, [tool]: visible }));
    writeDevelopmentToolVisibility(tool, visible);
    if (tool === "reactGrab") setReactGrabVisible(visible);
    if (tool === "reactScan") setReactScanVisible(visible);
    if (tool === "viteDevtools") setViteDevtoolsVisible(visible);
  }

  return (
    <AccountDialogProvider
      result={guestUpgrade}
      resultErrorCode={guestUpgrade === "failed" ? error : undefined}
      onResultDismissed={dismissGuestUpgradeResult}
    >
      <HeadContent />
      <AccountDeletionProvider>
        <TopUpProvider>
          {/* An account waiting to be deleted sees only its countdown, and a deleted one only that
              it's gone, with nothing else of Pax. An account on the waitlist sees only the
              waitlist, and an invite link opens on its own to let it in. Signed out, and while a
              new session is checked, the entry screen is the whole window. */}
          {pathname === ACCOUNT_DELETED_PATH ? (
            <Outlet />
          ) : session === null || checkPending ? (
            <EntryScreen checking={checkPending}>
              <Outlet />
            </EntryScreen>
          ) : isDeletionPending(session) ? (
            <AccountDeletionLock />
          ) : pathname.startsWith(INVITE_PATH_PREFIX) ? (
            <Outlet />
          ) : session && isWaitlisted(session) ? (
            <WaitlistScreen session={session} />
          ) : (
            <AppShell
              developmentTools={developmentTools}
              onDevelopmentToolVisibilityChange={handleDevelopmentToolVisibilityChange}
            >
              {connectionStatus === "online" ? (
                <Outlet />
              ) : (
                <OfflineStatus reason={connectionStatus} />
              )}
            </AppShell>
          )}
          <RiskDialog />
          {BotChecks &&
            developmentTools.botChecks &&
            window.location.hostname.endsWith("localhost") && (
              <Suspense fallback={null}>
                <BotChecks
                  onClose={() => handleDevelopmentToolVisibilityChange("botChecks", false)}
                />
              </Suspense>
            )}
        </TopUpProvider>
      </AccountDeletionProvider>
      {Devtools && developmentTools.tanstackDevtools && (
        <Suspense fallback={null}>
          <Devtools />
        </Suspense>
      )}
    </AccountDialogProvider>
  );
}
